/*
 * Odaline cart enhancements (cart drawer + cart page). Single IIFE, vanilla JS, loaded with defer.
 *
 * Ground rules (lessons from previous builds):
 * - Everything in the drawer / cart page is already correct from the Liquid render. Dawn replaces that markup
 *   from the server on every add / quantity change / remove, so this file never holds cart state; it only adds:
 *     1. "+ Add" for the "Complete the look" upsell (/cart/add.js + Dawn's own re-render path)
 *     2. desktop prev/next arrows for the upsell scroller
 *     3. a smooth free-shipping fill animation from the previous width to the new server-rendered width
 *     4. keeping the drawer and the /cart page in sync with each other (Dawn leaves one of them stale)
 * - All listeners are delegated on document, so they keep working after Dawn swaps the HTML.
 * - Freshly rendered markup is detected with a 1ms CSS animation (animationstart), NOT a MutationObserver.
 * - Runs once even if included twice (theme.liquid + the cart snippets both include it).
 */
(function () {
  'use strict';

  if (window.OdalineCart) return;

  var FILL_MS = 700;
  var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  /* ---------------------------------------------------------------- helpers */

  function $all(selector, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  function routeUrl(key, fallback) {
    return (window.routes && window.routes[key]) || fallback;
  }

  function pubEvents() {
    /* global PUB_SUB_EVENTS */
    return typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null;
  }

  function publishCartUpdate(payload) {
    var events = pubEvents();
    if (!events || typeof window.publish !== 'function') return;
    try {
      window.publish(events.cartUpdate, payload);
    } catch (e) {
      /* a failing third-party subscriber must not break the add flow */
    }
  }

  function parseSection(html, selector) {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  function genericError() {
    return (window.cartStrings && window.cartStrings.error) || 'Something went wrong. Please try again.';
  }

  function onNextFrame(fn) {
    if (window.requestAnimationFrame) window.requestAnimationFrame(fn);
    else setTimeout(fn, 16);
  }

  /* ------------------------------------------- Shopify standard cart events */

  function startLinesEvent(target, variantId, context) {
    var SE = window.StandardEvents || {};
    if (!SE.CartLinesUpdateEvent || !target) return null;
    try {
      var deferred = SE.CartLinesUpdateEvent.createPromise();
      target.dispatchEvent(
        new SE.CartLinesUpdateEvent({
          action: 'add',
          context: context,
          lines: [{ merchandiseId: String(variantId), quantity: 1 }],
          promise: deferred.promise,
        })
      );
      return deferred;
    } catch (e) {
      return null;
    }
  }

  function resolveLinesEvent(deferred) {
    if (!deferred) return;
    var SE = window.StandardEvents || {};
    fetch(routeUrl('cart_url', '/cart') + '.json', { credentials: 'same-origin' })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        if (!cart || !cart.currency) throw new Error('Missing currency in cart response');
        deferred.resolve({ cart: SE.CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
      })
      .catch(function (e) {
        deferred.reject(e);
      });
  }

  /* -------------------------------------------------- drawer / page sync */

  function syncEmptyState(count) {
    if (typeof count !== 'number') return;
    var empty = count === 0;
    if (empty) {
      // The bars are gone with the last item: the next one should grow from zero.
      lastPct = {};
      running = {};
    }
    var drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.classList.toggle('is-empty', empty);
    var pageItems = document.querySelector('cart-items');
    if (pageItems) pageItems.classList.toggle('is-empty', empty);
    var pageFooter = document.getElementById('main-cart-footer');
    if (pageFooter) pageFooter.classList.toggle('is-empty', empty);
  }

  function renderDrawerInner(html) {
    var inner = document.querySelector('#CartDrawer .drawer__inner');
    var source = parseSection(html, '.drawer__inner');
    if (!inner || !source) return false;
    inner.innerHTML = source.innerHTML;
    var drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.classList.toggle('is-empty', !!inner.querySelector('.drawer__inner-empty'));
    return true;
  }

  function renderBubble(html) {
    var bubble = document.getElementById('cart-icon-bubble');
    var source = parseSection(html, '.shopify-section');
    if (bubble && source) bubble.innerHTML = source.innerHTML;
  }

  var drawerSeq = 0;

  /* Re-renders the (closed) drawer + cart bubble from the server. */
  function refreshDrawer() {
    if (!document.querySelector('#CartDrawer .drawer__inner')) return Promise.resolve();
    var seq = ++drawerSeq;
    return fetch(routeUrl('cart_url', '/cart') + '?sections=cart-drawer,cart-icon-bubble', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (sections) {
        if (seq !== drawerSeq || !sections) return;
        if (sections['cart-drawer']) renderDrawerInner(sections['cart-drawer']);
        if (sections['cart-icon-bubble']) renderBubble(sections['cart-icon-bubble']);
      })
      .catch(function () {});
  }

  /* Dawn's CartItems / CartDrawerItems both ignore cart-update events whose source is 'cart-items', so on /cart
     the one the shopper did not touch goes stale. The open drawer means the change came from the drawer. */
  function syncCartPageAndDrawer() {
    var pageItems = document.querySelector('cart-items');
    var drawer = document.querySelector('cart-drawer');
    if (!pageItems || !drawer) return;
    if (drawer.classList.contains('active')) {
      if (typeof pageItems.onCartUpdate === 'function') pageItems.onCartUpdate();
    } else {
      refreshDrawer();
    }
  }

  /* ------------------------------------------------- upsell: one-tap add */

  function upsellError(scope, message) {
    var el = scope && scope.querySelector('[data-odl-upsell-error]');
    if (!el) return;
    el.textContent = message || '';
    el.hidden = !message;
  }

  function setBusy(button, busy) {
    if (busy) {
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-busy', 'true');
      button.classList.add('is-loading');
    } else {
      button.removeAttribute('aria-disabled');
      button.removeAttribute('aria-busy');
      button.classList.remove('is-loading');
    }
  }

  /* Which sections to ask /cart/add.js for, mirroring how Dawn renders each surface. */
  function renderPlan(button) {
    var pageItems = button.closest('cart-items');
    if (pageItems && typeof pageItems.getSectionsToRender === 'function') {
      try {
        var entries = pageItems.getSectionsToRender();
        var ids = entries.map(function (entry) {
          return entry.section;
        });
        // The Section Rendering API returns at most 5 sections per request.
        if (document.querySelector('#CartDrawer .drawer__inner') && ids.length < 5) ids.push('cart-drawer');
        return { mode: 'page', entries: entries, ids: ids };
      } catch (e) {
        /* fall through */
      }
    }
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.getSectionsToRender === 'function' && typeof drawer.renderContents === 'function') {
      return {
        mode: 'drawer',
        drawer: drawer,
        ids: drawer.getSectionsToRender().map(function (section) {
          return section.id;
        }),
      };
    }
    return { mode: 'redirect', ids: [] };
  }

  function addVariant(variantId, plan) {
    var body = { items: [{ id: variantId, quantity: 1 }] };
    if (plan.ids.length) {
      body.sections = plan.ids;
      body.sections_url = window.location.pathname;
    }
    return fetch(routeUrl('cart_add_url', '/cart/add') + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    }).then(function (response) {
      return response
        .json()
        .catch(function () {
          return null;
        })
        .then(function (data) {
          if (!response.ok || !data || data.status) {
            throw new Error((data && (data.description || data.message)) || genericError());
          }
          return data;
        });
    });
  }

  function announce(regionId, message) {
    setTimeout(function () {
      var region = document.getElementById(regionId);
      if (!region) return;
      region.setAttribute('aria-hidden', 'false');
      region.textContent = message;
    }, 150);
  }

  function renderIntoDrawer(plan, data, variantId, title) {
    publishCartUpdate({ source: 'odaline-upsell', productVariantId: String(variantId), cartData: data });
    if (!data.sections || !data.sections['cart-drawer']) {
      refreshDrawer();
      return;
    }
    plan.drawer.classList.remove('is-empty');
    plan.drawer.renderContents(data);
    setTimeout(function () {
      var container = document.getElementById('CartDrawer');
      var inner = container && container.querySelector('.drawer__inner');
      if (container && inner && typeof window.trapFocus === 'function') {
        try {
          window.trapFocus(container, inner);
        } catch (e) {
          inner.focus();
        }
      }
    }, 50);
    announce('CartDrawer-LiveRegionText', title + ' added to your bag');
  }

  function renderIntoPage(plan, data, variantId, title) {
    var sections = data.sections || {};
    var missing = false;
    plan.entries.forEach(function (entry) {
      var host = document.getElementById(entry.id);
      var source = parseSection(sections[entry.section], entry.selector);
      if (!host) return;
      if (!source) {
        missing = true;
        return;
      }
      var target = host.querySelector(entry.selector) || host;
      target.innerHTML = source.innerHTML;
    });
    if (missing) {
      window.location.reload();
      return;
    }
    if (sections['cart-drawer']) renderDrawerInner(sections['cart-drawer']);
    syncEmptyState(1);
    // Both Dawn cart surfaces are already re-rendered from this response: 'cart-items' keeps them from
    // fetching again, odalineRendered keeps our own page/drawer sync from doing so.
    publishCartUpdate({
      source: 'cart-items',
      productVariantId: String(variantId),
      cartData: data,
      odalineRendered: true,
    });
    var input = document.querySelector('#main-cart-items [data-quantity-variant-id="' + variantId + '"]');
    var line = input && input.closest('.cart-item');
    var name = line && line.querySelector('.cart-item__name');
    if (name) name.focus({ preventScroll: true });
    announce('cart-live-region-text', title + ' added to your bag');
  }

  function onUpsellAdd(button) {
    if (button.getAttribute('aria-disabled') === 'true') return;
    var scope = button.closest('[data-odl-upsell]');
    var item = button.closest('[data-odl-upsell-item]');
    var select = item && item.querySelector('[data-odl-upsell-select]');
    var variantId = parseInt(select ? select.value : button.getAttribute('data-variant-id'), 10);
    upsellError(scope, '');

    if (!variantId) {
      if (select) {
        var label = select.options[0] ? select.options[0].text.trim().toLowerCase() : 'option';
        select.setAttribute('aria-invalid', 'true');
        upsellError(scope, 'Choose your ' + label + ' first.');
        select.focus();
      }
      return;
    }

    var titleEl = item && item.querySelector('.odl-upsell__title');
    var title = titleEl ? titleEl.textContent.trim() : 'Item';
    var plan = renderPlan(button);
    var lines = startLinesEvent(button, variantId, plan.mode === 'page' ? 'cart' : 'dialog');

    setBusy(button, true);
    addVariant(variantId, plan)
      .then(function (data) {
        resolveLinesEvent(lines);
        if (plan.mode === 'page') {
          renderIntoPage(plan, data, variantId, title);
        } else if (plan.mode === 'drawer') {
          renderIntoDrawer(plan, data, variantId, title);
        } else {
          window.location.href = routeUrl('cart_url', '/cart');
        }
      })
      .catch(function (error) {
        if (lines) lines.reject(error);
        upsellError(scope, (error && error.message) || genericError());
        if (button.isConnected) setBusy(button, false);
      })
      .then(function () {
        if (button.isConnected) setBusy(button, false);
      });
  }

  /* Variant select in an upsell card. Captured on document so the change event never reaches Dawn's
     CartItems change handler (it would treat the select as a quantity input). */
  document.addEventListener(
    'change',
    function (event) {
      var select = event.target && event.target.closest ? event.target.closest('[data-odl-upsell-select]') : null;
      if (!select) return;
      event.stopPropagation();
      select.removeAttribute('aria-invalid');
      var item = select.closest('[data-odl-upsell-item]');
      var option = select.options[select.selectedIndex];
      var price = item && item.querySelector('[data-odl-upsell-price]');
      if (price && option && option.getAttribute('data-price')) price.textContent = option.getAttribute('data-price');
      var button = item && item.querySelector('[data-odl-upsell-add]');
      if (button) button.setAttribute('data-variant-id', select.value);
      upsellError(select.closest('[data-odl-upsell]'), '');
    },
    true
  );

  /* ---------------------------------------------- upsell: desktop arrows */

  function updateArrows(upsell) {
    var track = upsell && upsell.querySelector('[data-odl-upsell-track]');
    if (!track) return;
    var max = track.scrollWidth - track.clientWidth;
    var scrollable = max > 2;
    upsell.classList.toggle('is-scrollable', scrollable);
    var prev = upsell.querySelector('[data-odl-upsell-prev]');
    var next = upsell.querySelector('[data-odl-upsell-next]');
    if (prev) prev.disabled = !scrollable || track.scrollLeft <= 2;
    if (next) next.disabled = !scrollable || track.scrollLeft >= max - 2;
  }

  function updateAllArrows() {
    $all('[data-odl-upsell]').forEach(updateArrows);
  }

  function scrollUpsell(upsell, direction) {
    var track = upsell.querySelector('[data-odl-upsell-track]');
    if (!track) return;
    var card = track.querySelector('[data-odl-upsell-item]');
    var gap = parseFloat(window.getComputedStyle(track).columnGap) || 12;
    var step = card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
    track.scrollBy({ left: direction * step, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  var scrollQueued = false;
  document.addEventListener(
    'scroll',
    function (event) {
      var track = event.target;
      if (!track || !track.matches || !track.matches('[data-odl-upsell-track]') || scrollQueued) return;
      scrollQueued = true;
      onNextFrame(function () {
        scrollQueued = false;
        updateArrows(track.closest('[data-odl-upsell]'));
      });
    },
    { capture: true, passive: true }
  );

  /* Desktop safety net: measure a scroller the first time the pointer reaches it (fresh markup has no flag). */
  document.addEventListener('mouseover', function (event) {
    var upsell = event.target && event.target.closest ? event.target.closest('[data-odl-upsell]') : null;
    if (!upsell || upsell.__odlMeasured) return;
    upsell.__odlMeasured = true;
    updateArrows(upsell);
  });

  var resizeQueued = false;
  window.addEventListener('resize', function () {
    if (resizeQueued) return;
    resizeQueued = true;
    onNextFrame(function () {
      resizeQueued = false;
      updateAllArrows();
    });
  });

  /* ------------------------------------------------------------- clicks */

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var add = target.closest('[data-odl-upsell-add]');
    if (add && add.tagName === 'BUTTON') {
      event.preventDefault();
      onUpsellAdd(add);
      return;
    }

    var prev = target.closest('[data-odl-upsell-prev]');
    var next = target.closest('[data-odl-upsell-next]');
    if (prev || next) {
      event.preventDefault();
      var upsell = (prev || next).closest('[data-odl-upsell]');
      if (upsell) scrollUpsell(upsell, prev ? -1 : 1);
    }
  });

  /* ------------------------------------------ free-shipping fill animation */

  var lastPct = {};
  var running = {};

  function easeOut(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function stopTransitionBubble(event) {
    // Dawn's CartDrawer.open() waits for the drawer's own transitionend; ours must not trigger it.
    event.stopPropagation();
  }

  function contextOf(fill) {
    var bar = fill.closest('[data-odl-shipbar]');
    return (bar && bar.getAttribute('data-context')) || 'drawer';
  }

  /* Records the widths already on the page so only re-rendered bars animate. */
  function markFills(root) {
    $all('[data-odl-shipbar-fill]', root).forEach(function (fill) {
      fill.__odlSeen = true;
      var pct = parseFloat(fill.getAttribute('data-pct'));
      if (!isNaN(pct)) lastPct[contextOf(fill)] = pct;
    });
  }

  function onFillMounted(fill) {
    if (fill.__odlSeen) return;
    fill.__odlSeen = true;
    var key = contextOf(fill);
    var target = parseFloat(fill.getAttribute('data-pct'));
    if (isNaN(target)) return;

    var from = Object.prototype.hasOwnProperty.call(lastPct, key) ? lastPct[key] : 0;
    var duration = FILL_MS;
    var now = Date.now();
    var current = running[key];
    if (current && now - current.start < current.duration) {
      // Dawn often renders the drawer twice after an add (renderContents, then its cart-update refresh).
      // Continue the animation that is already on its way instead of jumping.
      var progress = (now - current.start) / current.duration;
      from = current.from + (current.to - current.from) * easeOut(progress);
      if (current.to === target) duration = Math.max(120, current.duration - (now - current.start));
    }
    lastPct[key] = target;

    if (reduceMotion || Math.abs(from - target) < 0.5) {
      running[key] = null;
      return;
    }

    fill.addEventListener('transitionend', stopTransitionBubble);
    fill.style.transition = 'none';
    fill.style.width = from + '%';
    void fill.offsetWidth; // commit the start width so the transition runs
    fill.style.transition = 'width ' + Math.round(duration) + 'ms cubic-bezier(0.22, 0.61, 0.36, 1)';
    fill.style.width = target + '%';
    running[key] = { from: from, to: target, start: now, duration: duration };
  }

  /* Fresh markup announces itself through a 1ms CSS animation (see assets/odaline-cart.css). */
  document.addEventListener('animationstart', function (event) {
    var name = event.animationName;
    if (name === 'odl-cart-mount-fill') {
      onFillMounted(event.target);
    } else if (name === 'odl-cart-mount-track') {
      updateArrows(event.target.closest('[data-odl-upsell]'));
    }
  });

  /* ------------------------------------------------------- subscriptions */

  function subscribeToCart() {
    var events = pubEvents();
    if (!events || typeof window.subscribe !== 'function') return;
    window.subscribe(events.cartUpdate, function (event) {
      var data = event && event.cartData;
      if (data && typeof data.item_count === 'number') syncEmptyState(data.item_count);
      if (event && event.source === 'cart-items' && !event.odalineRendered) syncCartPageAndDrawer();
      // Dawn re-renders right after (or just before) publishing; re-measure the new scrollers once it has.
      setTimeout(updateAllArrows, 450);
    });
  }

  /* Back / forward cache: the cart may have changed on another page. */
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    var drawer = document.querySelector('cart-drawer');
    if (drawer && !drawer.classList.contains('active')) refreshDrawer();
    var pageItems = document.querySelector('cart-items');
    if (pageItems && typeof pageItems.onCartUpdate === 'function') pageItems.onCartUpdate();
  });

  document.addEventListener('shopify:section:load', function (event) {
    markFills(event.target);
    updateAllArrows();
  });

  /* ------------------------------------------------------------------ init */

  markFills(document);

  var booted = false;
  function boot() {
    if (booted) return;
    booted = true;
    subscribeToCart();
    updateAllArrows();
  }

  // Deferred scripts run before DOMContentLoaded, when Dawn's pubsub/constants are guaranteed to exist.
  document.addEventListener('DOMContentLoaded', boot);
  window.addEventListener('load', boot);
  if (document.readyState === 'complete') boot();

  window.OdalineCart = {
    refreshDrawer: refreshDrawer,
    updateUpsellArrows: updateAllArrows,
  };
})();
